import { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import {
  useAuditoriaPeriodo,
  useRelatorioConciliacao,
  useHistoricoAjustes,
  useAjustarDivergencia,
  useFecharConciliacao,
} from '@/features/pdv/hooks/useConciliacao';
import type { DivergenciaForma } from '@/features/pdv/types/pdv.types';
import { formatCurrency } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';

const hoje = new Date().toISOString().split('T')[0];
const seteDiasAtras = new Date(Date.now() - 7 * 86_400_000).toISOString().split('T')[0];

// ════════════════════════════════════════════════════════════════════════════
//  ABA 1 — AUDITORIA
// ════════════════════════════════════════════════════════════════════════════

function AbaAuditoria() {
  const { user } = useAuth();
  const empresaId = user?.empresa.id ?? 0;
  const [dtInicio, setDtInicio] = useState(seteDiasAtras);
  const [dtFim, setDtFim]       = useState(hoje);
  const [buscando, setBuscando] = useState(false);

  const { data, isLoading, refetch } = useAuditoriaPeriodo(
    empresaId, dtInicio, dtFim, buscando && empresaId > 0,
  );

  const [ajusteAberto, setAjusteAberto] = useState(false);
  const [linhaAjuste, setLinhaAjuste]   = useState<DivergenciaForma | null>(null);
  const [turnoIdAjuste, setTurnoIdAjuste] = useState('');
  const [valorAjuste, setValorAjuste]     = useState('');
  const [motivoAjuste, setMotivoAjuste]   = useState('');

  const ajustar = useAjustarDivergencia();

  function abrirAjuste(linha: DivergenciaForma) {
    setLinhaAjuste(linha);
    setValorAjuste(String(Math.abs(linha.diferenca)));
    setMotivoAjuste('');
    setTurnoIdAjuste('');
    setAjusteAberto(true);
  }

  async function confirmarAjuste() {
    if (!linhaAjuste) return;
    const id = Number(turnoIdAjuste);
    if (!id) return;
    await ajustar.mutateAsync({
      turnoId: id,
      dto: {
        formaPagamento: linhaAjuste.formaPagamento,
        valorAjuste: Number(valorAjuste),
        motivo: motivoAjuste,
      },
    });
    setAjusteAberto(false);
    refetch();
  }

  return (
    <div className="space-y-4">
      {/* Filtros */}
      <div className="flex flex-wrap gap-4 items-end">
        <div className="space-y-1">
          <Label>Data início</Label>
          <Input type="date" value={dtInicio} onChange={e => setDtInicio(e.target.value)} className="w-40" />
        </div>
        <div className="space-y-1">
          <Label>Data fim</Label>
          <Input type="date" value={dtFim} onChange={e => setDtFim(e.target.value)} className="w-40" />
        </div>
        <Button onClick={() => { setBuscando(true); refetch(); }} disabled={isLoading}>
          {isLoading ? 'Buscando…' : 'Auditar'}
        </Button>
      </div>

      {/* Tabela de divergências */}
      {data && (
        <>
          {data.temDivergencia
            ? <Badge variant="destructive">Divergências encontradas</Badge>
            : <Badge variant="outline" className="text-green-600 border-green-600">Sem divergências</Badge>
          }
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Forma de Pagamento</TableHead>
                <TableHead className="text-right">Esperado (sistema)</TableHead>
                <TableHead className="text-right">Contado (turno)</TableHead>
                <TableHead className="text-right">Diferença</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.divergencias.map(d => (
                <TableRow key={d.formaPagamento}>
                  <TableCell className="font-medium">{d.formaPagamento}</TableCell>
                  <TableCell className="text-right">{formatCurrency(d.esperado)}</TableCell>
                  <TableCell className="text-right">{formatCurrency(d.contado)}</TableCell>
                  <TableCell
                    className={`text-right font-semibold ${
                      d.temDivergencia ? 'text-red-600' : 'text-green-600'
                    }`}
                  >
                    {formatCurrency(d.diferenca)}
                  </TableCell>
                  <TableCell>
                    {d.temDivergencia && (
                      <Button size="sm" variant="outline" onClick={() => abrirAjuste(d)}>
                        Ajustar
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}

      {/* Dialog de ajuste */}
      <Dialog open={ajusteAberto} onOpenChange={setAjusteAberto}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Ajustar divergência — {linhaAjuste?.formaPagamento}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>ID do Turno</Label>
              <Input
                type="number"
                placeholder="Ex: 42"
                value={turnoIdAjuste}
                onChange={e => setTurnoIdAjuste(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label>Valor do ajuste (R$)</Label>
              <Input
                type="number"
                step="0.01"
                value={valorAjuste}
                onChange={e => setValorAjuste(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label>Motivo (mín. 20 caracteres)</Label>
              <Textarea
                rows={3}
                value={motivoAjuste}
                onChange={e => setMotivoAjuste(e.target.value)}
                placeholder="Descreva o motivo do ajuste…"
              />
              <p className={`text-xs ${motivoAjuste.trim().length < 20 ? 'text-red-500' : 'text-muted-foreground'}`}>
                {motivoAjuste.trim().length}/20 caracteres mínimos
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAjusteAberto(false)}>Cancelar</Button>
            <Button
              onClick={confirmarAjuste}
              disabled={motivoAjuste.trim().length < 20 || !turnoIdAjuste || ajustar.isPending}
            >
              {ajustar.isPending ? 'Salvando…' : 'Confirmar Ajuste'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  ABA 2 — RELATÓRIO DE MOVIMENTAÇÃO
// ════════════════════════════════════════════════════════════════════════════

function AbaRelatorio() {
  const { user } = useAuth();
  const empresaId = user?.empresa.id ?? 0;
  const [dtInicio, setDtInicio]   = useState(seteDiasAtras);
  const [dtFim, setDtFim]         = useState(hoje);
  const [turnoId, setTurnoId]     = useState('');
  const [buscando, setBuscando]   = useState(false);

  const { data, isLoading, refetch } = useRelatorioConciliacao(
    empresaId, dtInicio, dtFim,
    turnoId ? Number(turnoId) : undefined,
    buscando && empresaId > 0,
  );

  return (
    <div className="space-y-4">
      {/* Filtros */}
      <div className="flex flex-wrap gap-4 items-end">
        <div className="space-y-1">
          <Label>Data início</Label>
          <Input type="date" value={dtInicio} onChange={e => setDtInicio(e.target.value)} className="w-40" />
        </div>
        <div className="space-y-1">
          <Label>Data fim</Label>
          <Input type="date" value={dtFim} onChange={e => setDtFim(e.target.value)} className="w-40" />
        </div>
        <div className="space-y-1">
          <Label>Turno (opcional)</Label>
          <Input
            type="number"
            placeholder="ID do turno"
            value={turnoId}
            onChange={e => setTurnoId(e.target.value)}
            className="w-32"
          />
        </div>
        <Button onClick={() => { setBuscando(true); refetch(); }} disabled={isLoading}>
          {isLoading ? 'Gerando…' : 'Gerar Relatório'}
        </Button>
      </div>

      {/* KPIs */}
      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Saldo Inicial',    value: data.saldoInicial,    color: '' },
              { label: 'Total Entradas',   value: data.totalEntradas,   color: 'text-green-600' },
              { label: 'Total Saídas',     value: data.totalSaidas,     color: 'text-red-600' },
              { label: 'Saldo Final',      value: data.saldoFinal,      color: data.saldoFinal >= 0 ? 'text-green-600' : 'text-red-600' },
            ].map(k => (
              <Card key={k.label}>
                <CardHeader className="pb-1 pt-3 px-4">
                  <CardTitle className="text-sm text-muted-foreground">{k.label}</CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-3">
                  <p className={`text-lg font-bold ${k.color}`}>{formatCurrency(k.value)}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data/Hora</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Descrição</TableHead>
                <TableHead>Forma Pgto</TableHead>
                <TableHead className="text-right">Valor</TableHead>
                <TableHead className="text-right">Saldo</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.lancamentos.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    Nenhum lançamento encontrado no período.
                  </TableCell>
                </TableRow>
              )}
              {data.lancamentos.map((l, i) => (
                <TableRow key={i}>
                  <TableCell className="text-sm">
                    {new Date(l.dtMovimento).toLocaleString('pt-BR')}
                  </TableCell>
                  <TableCell>
                    <Badge variant={l.tipo === 'Entrada' ? 'outline' : 'secondary'}>
                      {l.tipo}
                    </Badge>
                  </TableCell>
                  <TableCell className="max-w-48 truncate">{l.descricao}</TableCell>
                  <TableCell>{l.formaPagamento}</TableCell>
                  <TableCell className={`text-right font-mono ${l.tipo === 'Entrada' ? 'text-green-600' : 'text-red-600'}`}>
                    {l.tipo === 'Saída' ? '−' : '+'}{formatCurrency(l.valor)}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {formatCurrency(l.saldoAcumulado)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  ABA 3 — HISTÓRICO DE AJUSTES
// ════════════════════════════════════════════════════════════════════════════

function AbaHistoricoAjustes() {
  const { user } = useAuth();
  const [turnoId, setTurnoId]         = useState('');
  const [turnoIdBusca, setTurnoIdBusca] = useState<number | undefined>();
  const [confirmarFechar, setConfirmarFechar] = useState(false);

  const { data, isLoading } = useHistoricoAjustes(turnoIdBusca);
  const fechar = useFecharConciliacao();

  const turnoFechado = data?.some(a => a.isFechado) ?? false;

  async function confirmarFechamento() {
    if (!turnoIdBusca || !user) return;
    await fechar.mutateAsync({ turnoId: turnoIdBusca, dto: { operadorId: user.id } });
    setConfirmarFechar(false);
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-4 items-end">
        <div className="space-y-1">
          <Label>ID do Turno</Label>
          <Input
            type="number"
            placeholder="Ex: 42"
            value={turnoId}
            onChange={e => setTurnoId(e.target.value)}
            className="w-40"
          />
        </div>
        <Button
          onClick={() => setTurnoIdBusca(Number(turnoId))}
          disabled={!turnoId || isLoading}
        >
          {isLoading ? 'Buscando…' : 'Buscar'}
        </Button>
        {turnoIdBusca && !turnoFechado && (
          <Button
            variant="destructive"
            onClick={() => setConfirmarFechar(true)}
            disabled={fechar.isPending}
          >
            Fechar Conciliação do Turno
          </Button>
        )}
        {turnoFechado && (
          <Badge variant="secondary" className="text-orange-600">Conciliação Fechada</Badge>
        )}
      </div>

      {data && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Data/Hora</TableHead>
              <TableHead>Operador</TableHead>
              <TableHead>Forma</TableHead>
              <TableHead className="text-right">Valor Ajuste</TableHead>
              <TableHead>Motivo</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                  Nenhum ajuste registrado para este turno.
                </TableCell>
              </TableRow>
            )}
            {data.map(a => (
              <TableRow key={a.id}>
                <TableCell className="text-sm">
                  {new Date(a.dtAjuste).toLocaleString('pt-BR')}
                </TableCell>
                <TableCell>#{a.operadorId}</TableCell>
                <TableCell>{a.formaPagamento}</TableCell>
                <TableCell className={`text-right font-mono ${a.valorAjuste >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(a.valorAjuste)}
                </TableCell>
                <TableCell className="italic text-sm text-muted-foreground max-w-64 truncate">
                  {a.motivo}
                </TableCell>
                <TableCell>
                  <Badge variant={a.isFechado ? 'secondary' : 'outline'}>
                    {a.status}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <AlertDialog open={confirmarFechar} onOpenChange={setConfirmarFechar}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Fechar Conciliação?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação encerrará a conciliação do turno #{turnoIdBusca} e impedirá novos ajustes.
              Esta operação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmarFechamento} className="bg-destructive text-destructive-foreground">
              Confirmar Fechamento
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  PÁGINA PRINCIPAL
// ════════════════════════════════════════════════════════════════════════════

export default function ConciliacaoCaixa() {
  return (
    <div className="p-6 space-y-4">
      <h1 className="text-2xl font-bold">Conciliação de Caixa</h1>

      <Tabs defaultValue="auditoria">
        <TabsList className="mb-4">
          <TabsTrigger value="auditoria">Auditoria</TabsTrigger>
          <TabsTrigger value="relatorio">Relatório de Movimentação</TabsTrigger>
          <TabsTrigger value="historico">Histórico de Ajustes</TabsTrigger>
        </TabsList>

        <TabsContent value="auditoria">
          <Card>
            <CardHeader>
              <CardTitle>Auditoria de Divergências por Forma de Pagamento</CardTitle>
            </CardHeader>
            <CardContent>
              <AbaAuditoria />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="relatorio">
          <Card>
            <CardHeader>
              <CardTitle>Relatório de Movimentação de Caixa</CardTitle>
            </CardHeader>
            <CardContent>
              <AbaRelatorio />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="historico">
          <Card>
            <CardHeader>
              <CardTitle>Histórico de Ajustes de Conciliação</CardTitle>
            </CardHeader>
            <CardContent>
              <AbaHistoricoAjustes />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
